import { ReactNode, useState, useEffect, useCallback } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRole?: 'admin' | 'subcontractor'
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, profile, loading, refreshProfile, effectiveRole } = useAuth()
  const location = useLocation()
  const [profileLoadTimeout, setProfileLoadTimeout] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)

  // Give profile extra time to load if user exists but profile doesn't
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (user && !profile && !loading && !isRetrying) {
      // Wait up to 5 seconds for profile to load before showing error
      timer = setTimeout(() => {
        setProfileLoadTimeout(true)
      }, 5000)
    } else if (profile) {
      // Reset timeout state when profile loads
      setProfileLoadTimeout(false)
      setRetryCount(0)
    }
    return () => clearTimeout(timer)
  }, [user, profile, loading, isRetrying])

  // Handle retry with the new refreshProfile function
  const handleRetry = useCallback(async () => {
    setIsRetrying(true)
    setProfileLoadTimeout(false)
    setRetryCount(prev => prev + 1)
    
    try {
      await refreshProfile()
    } catch (error) {
      console.error('Retry failed:', error)
    } finally {
      setIsRetrying(false)
    }
  }, [refreshProfile])

  // Still loading - show spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nature-50 dark:bg-nature-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar...</p>
        </div>
      </div>
    )
  }

  // No user at all - definitely need to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // User exists but profile is still loading - wait a bit more
  // This prevents logout during slow profile fetches
  if (!profile && !profileLoadTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nature-50 dark:bg-nature-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">
            {isRetrying ? 'Försöker igen...' : 'Laddar profil...'}
          </p>
        </div>
      </div>
    )
  }

  // Profile failed to load after timeout - show error but DON'T logout
  // User might have network issues, don't punish them by logging out
  if (!profile && profileLoadTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nature-50 dark:bg-nature-900">
        <div className="text-center max-w-md p-6 bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-nature-900 dark:text-nature-100 mb-2">
            Kunde inte ladda profil
          </h2>
          <p className="text-nature-600 dark:text-nature-400 mb-4">
            Det gick inte att hämta din profil. Kontrollera din internetanslutning och försök igen.
          </p>
          {retryCount > 0 && (
            <p className="text-sm text-nature-500 dark:text-nature-400 mb-4">
              Försök {retryCount} av 3
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleRetry}
              disabled={isRetrying || retryCount >= 3}
              className="btn-primary disabled:opacity-50"
            >
              {isRetrying ? 'Försöker...' : 'Försök igen'}
            </button>
            {retryCount >= 3 && (
              <button
                onClick={() => window.location.reload()}
                className="btn-secondary"
              >
                Ladda om sidan
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Check role if required - use effectiveRole which considers viewMode for admins
  if (requiredRole && profile && effectiveRole !== requiredRole) {
    const redirectPath = effectiveRole === 'admin' ? '/admin' : '/subcontractor'
    return <Navigate to={redirectPath} replace />
  }

  // All checks passed - render children
  return <>{children}</>
}
